<ng-template #stateBody>
    <!-- Product states (PD §5): 3 skeleton cards (P/proto-states.js:130), then the error panel. -->
    @if (loading) {
        <agu-skeleton [Count]="3" />
    } @else if (error) {
        <agu-state-panel
            Variant="error"
            [Icon]="ErrorCopy.Icon"
            [Title]="ErrorCopy.Title"
            [Body]="ErrorCopy.Body"
            PrimaryLabel="Try again"
            [SecondaryLabel]="failures.SupportHref ? 'Contact support' : null"
            [SecondaryHref]="failures.SupportHref"
            [Foot]="failures.Foot"
            [Note]="failures.Note"
            (Primary)="Retry()"
        />
    } @else if (!profile) {
        <agu-state-panel Title="This member's record isn't available." />
    } @else {
        <!--
          The mockup's `.top`: a bare 27px/800 h1 over one 13.5px sub line, no
          icon chip and no band (parity/my-profile.md §4.1).
        -->
        <agu-plain-header Title="My member hub profile" [Sub]="HeaderSub" />

        <!-- The mockup's `.selfbar`: a blue-tint flex bar with an eye glyph and a plain bold link — not an alert with an outlined button (parity/my-profile.md §4.2). -->
        <div class="ra-mp__selfbar">
                <span class="ra-mp__selfbar-eye" aria-hidden="true">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12z" />
                        <circle cx="12" cy="12" r="2.6" />
                    </svg>
                </span>
                <p>
                    You are viewing your own record as the community sees it. Everything here comes from your AGU profile, which is where you change what is
                    shared.
                </p>
                @if (accountSettingsUrl) {
                    <a [href]="accountSettingsUrl" target="_blank" rel="noopener">Account settings <span aria-hidden="true">↗</span></a>
                }
        </div>

        <div class="ra-mp__grid">
            <div class="ra-mp__main">
                <div class="ra-mp__card ra-mp__pcard">
                    <div class="ra-mp__phead">
                        <div class="ra-mp__avatar" aria-hidden="true">{{ profile.initials }}</div>
                        <h2 class="ra-mp__name">{{ profile.name }}</h2>
                        <p class="ra-mp__role">
                            {{ AffiliationLine }}@if (profile.location) {<br />{{ profile.location }}}
                        </p>
                    </div>

                    <!--
                      The mockup's `.netline` also carries "Open to mentoring" and
                      "Open to co-investigator roles". No wire field backs either
                      (parity/my-profile.md §6 marks both ⚠ "no known field backs
                      this"), so neither is rendered rather than invented (D-F6).
                    -->
                    <div class="ra-mp__netline">
                        <span class="ra-mp__degpill">
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L9 17l-4 1 1-4z" />
                                <path d="M14.5 5.5l3 3" />
                            </svg>
                            {{ CoauthorChip }}
                        </span>
                    </div>

                    @if (HasLinks) {
                        <div class="ra-mp__rlinks">
                            @if (profile.orcid) {
                                <a [href]="'https://orcid.org/' + profile.orcid" target="_blank" rel="noopener">
                                    <span class="ra-mp__mk ra-mp__mk--orcid" aria-hidden="true">iD</span>ORCID
                                </a>
                            }
                            @if (profile.linkedInUrl) {
                                <a [href]="profile.linkedInUrl" target="_blank" rel="noopener">
                                    <span class="ra-mp__mk ra-mp__mk--li" aria-hidden="true">in</span>LinkedIn
                                </a>
                            }
                            @if (profile.aguProfileUrl) {
                                <!-- Interim lettermark: swap for the real AGU logo file when it is in the project (the mockup's own note on `.mk.agu`). -->
                                <a [href]="profile.aguProfileUrl" target="_blank" rel="noopener">
                                    <span class="ra-mp__mk ra-mp__mk--agu" aria-hidden="true">AGU</span>Public Profile
                                </a>
                            }
                        </div>
                    }
                </div>

                <div class="ra-mp__card ra-mp__sec">
                    <h2>Research areas</h2>
                    <p class="ra-mp__lede">Derived from your published works and abstracts, this is what your matching is based on.</p>
                    @if (profile.researchAreas.length > 0) {
                        <div class="ra-mp__topics">
                            @for (area of profile.researchAreas; track $index) {
                                <span class="ra-mp__topic">{{ area }}</span>
                            }
                        </div>
                    } @else {
                        <p class="ra-mp__empty">No research areas yet — they appear once your publications are linked.</p>
                    }
                </div>

                <!-- The server's profile rows carry no id, and titles repeat (two convened sessions both titled "Recent Advances in GeoHealth" raised NG0955 on every pass, 2026-10-05 stage sanity). These lists are rebuilt whole per load and never reordered in place, so position is the unique key. -->
                @if (workRows.length > 0) {
                    <div class="ra-mp__card ra-mp__sec">
                        <h2>Recent works</h2>
                        <p class="ra-mp__lede">{{ recentWorksSubtitle }}</p>
                        <ul class="ra-mp__works">
                            @for (row of workRows; track $index) {
                                <li>
                                    @if (row.url) {
                                        <a class="ra-mp__work-title" [href]="row.url" target="_blank" rel="noopener">{{ row.title }}</a>
                                    } @else {
                                        <div class="ra-mp__work-title">{{ row.title }}</div>
                                    }
                                    @if (row.metaLine) {
                                        <div class="ra-mp__work-meta">{{ row.metaLine }}</div>
                                    }
                                </li>
                            }
                        </ul>
                    </div>
                }

                @if (HasFundingService) {
                    <div class="ra-mp__card ra-mp__sec">
                        <h2>Funding &amp; service</h2>
                        <p class="ra-mp__lede">Public awards and AGU contributions. Award amounts are never shown to other members.</p>
                        <dl class="ra-mp__kv">
                            @if (profile.funding.length > 0) {
                                <dt>Current awards</dt>
                                <dd>
                                    @for (f of profile.funding; track $index) {
                                        <div>{{ FundingLine(f) }}</div>
                                    }
                                </dd>
                            }
                            @if (profile.sessionsConvened.length > 0) {
                                <dt>Sessions convened</dt>
                                <dd>
                                    @for (s of profile.sessionsConvened; track $index) {
                                        <div>{{ SessionLine(s) }}</div>
                                    }
                                </dd>
                            }
                            @if (profile.peerReview) {
                                <dt>Peer review</dt>
                                <dd>{{ PeerReviewLine(profile.peerReview) }}</dd>
                            }
                            @if (profile.membershipSince) {
                                <dt>AGU membership</dt>
                                <dd>Member since {{ profile.membershipSince }}</dd>
                            }
                        </dl>
                    </div>
                }
            </div>

            <div class="ra-mp__side">
                <div class="ra-mp__card ra-mp__side-card">
                    <h3>How you appear in matching</h3>
                    <ul class="ra-mp__stats">
                        <li><span>Works on record</span><b>{{ profile.stats.worksOnRecord }}</b></li>
                        <li><span>Co-authors</span><b>{{ profile.stats.coauthors }}</b></li>
                        <li><span>Members you connect</span><b>{{ profile.stats.membersYouConnect }}</b></li>
                    </ul>
                    @if (accountSettingsUrl) {
                        <a class="ra-mp__editcta" [href]="accountSettingsUrl" target="_blank" rel="noopener">
                            Account settings <span aria-hidden="true">↗</span>
                        </a>
                    }
                </div>

                    <div class="ra-mp__card ra-mp__side-card">
                        <h3>What's shared</h3>
                        <p class="ra-mp__visnote">Set on your AGU profile.</p>
                        <ul class="ra-mp__vis">
                            @for (row of ShareRows; track row.label) {
                                <li [class.ra-mp__vis-row--off]="!row.shared">
                                    @if (row.shared) {
                                        <span class="ra-mp__vis-mark" aria-hidden="true">
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round">
                                                <path d="M20 6 9 17l-5-5" />
                                            </svg>
                                        </span>
                                        {{ row.label }}
                                    } @else {
                                        <span class="ra-mp__vis-mark" aria-hidden="true">
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                                                <path d="M18 6 6 18M6 6l12 12" />
                                            </svg>
                                        </span>
                                        <span>{{ row.label }} <b>(not shared)</b></span>
                                    }
                                </li>
                            }
                        </ul>
                    </div>
            </div>
        </div>
    }
</ng-template>

<mj-page-body>
    <ng-container *ngTemplateOutlet="stateBody" />
</mj-page-body>
